import type { RosterNotice } from '../utils/parseStructuredFilesForHeadcount'
import { groupNoticesByFile, needsSheetPrefix } from '../utils/parseNotices'

interface Props {
  notices: RosterNotice[]
}

/**
 * What the roster parser found, shown before generation starts. Errors and
 * warnings are separate blocks, and a file never appears in both: a file that
 * failed shows why, a file that parsed shows what changed.
 */
export default function RosterNotices({ notices }: Props) {
  const errors = notices.filter((n) => n.severity === 'error')
  const warnings = notices.filter((n) => n.severity === 'warning')
  if (errors.length === 0 && warnings.length === 0) return null

  return (
    <div className="mt-3 space-y-2">
      {errors.length > 0 && (
        <NoticeBlock
          tone="error"
          title="Roster could not be read"
          hint="Its headcount could not be read. Fix the column names and re-upload, otherwise the AI may estimate headcount from this file."
          notices={errors}
        />
      )}
      {warnings.length > 0 && (
        <NoticeBlock
          tone="warning"
          title={`${warnings.length} ${warnings.length === 1 ? 'note' : 'notes'} on the roster`}
          notices={warnings}
        />
      )}
    </div>
  )
}

function NoticeBlock({ tone, title, hint, notices }: {
  tone: 'error' | 'warning'
  title: string
  hint?: string
  notices: RosterNotice[]
}) {
  const palette = tone === 'error'
    ? { box: 'bg-red-50 border-red-200', title: 'text-red-800', text: 'text-red-700', muted: 'text-red-500' }
    : { box: 'bg-amber-50 border-amber-200', title: 'text-amber-800', text: 'text-amber-800', muted: 'text-amber-600' }

  return (
    <div role={tone === 'error' ? 'alert' : 'status'} className={`p-3 border rounded-lg ${palette.box}`}>
      <p className={`text-xs font-semibold ${palette.title}`}>{title}</p>
      {hint && <p className={`text-[11px] mt-0.5 ${palette.text}`}>{hint}</p>}
      {groupNoticesByFile(notices).map(([fileName, group]) => (
        <div key={fileName} className="mt-1.5">
          <p className={`text-xs font-medium ${palette.title}`}>{fileName}</p>
          <ul className="pl-3">
            {group.map((notice, i) => (
              <li key={i} className={`text-xs list-disc list-outside ml-2 ${palette.text}`}>
                {needsSheetPrefix(notice) ? <span className={palette.muted}>sheet "{notice.sheet}" — </span> : null}
                <span className="whitespace-pre-wrap">{notice.message}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}
